/**
 * KalicilikService — Üretim belgeleri (teklif/iş emri/muayene/üretim kartı) kalıcılığı.
 * Offline-first: kaydet → localStorage HEMEN + DB (hareketler) sync; listele → DB çek + local cache birleştir.
 * DB erişimi mevcut FikoYTRequestService üzerinden (lib doğrudan kullanabilir — providedIn root).
 */
import { Injectable, inject } from '@angular/core';
import { FikoYTRequestService } from '@/src/app/shared/services/fikoyt-request.service';
import { FikoYTService } from '@/src/service/fikoyt.service';

export type KayitTip = 'teklif' | 'is_emri' | 'muayene' | 'uretim_karti';

export interface IKayit {
  id: string;            // istemci üretimi benzersiz id (dedupe için)
  tip: KayitTip;
  baslik: string;        // parça adı / no
  veri: any;             // modül durumu (geri yükleme için)
  tarih: string;         // ISO
  _id?: string;          // MongoDB _id (DB'den gelince)
  _sync?: boolean;       // DB'ye yazıldı mı
}

const KOMUT = 'FikoYT.sistem.DB.hareketler';

@Injectable({ providedIn: 'root' })
export class KalicilikService {
  private req = inject(FikoYTRequestService);
  private fy = inject(FikoYTService);
  private yuklenecekler = new Map<KayitTip, any>();   // "Yükle" → modül init'te okur

  private sahipId(): string { try { return this.fy.kimlik?.aktifKullaniciJson?.()?._id || ''; } catch { return ''; } }
  private vt(tip: KayitTip): string { return 'FikoYT.sistem.uretim.' + tip; }
  private lsKey(tip: KayitTip): string { return 'fiko_kalicilik_' + tip; }
  private id(): string { return 'k' + Date.now().toString(36) + Math.floor(Math.random() * 1e6).toString(36); }

  // ───────── localStorage ─────────
  private localOku(tip: KayitTip): IKayit[] {
    try { return JSON.parse(localStorage.getItem(this.lsKey(tip)) || '[]'); } catch { return []; }
  }
  private localSet(tip: KayitTip, liste: IKayit[]): void {
    try { localStorage.setItem(this.lsKey(tip), JSON.stringify(liste.slice(0, 300))); } catch { }
  }
  private localUpsert(tip: KayitTip, kayit: IKayit): void {
    const l = this.localOku(tip); const i = l.findIndex(k => k.id === kayit.id);
    if (i >= 0) l[i] = kayit; else l.unshift(kayit);
    this.localSet(tip, l);
  }

  // ───────── API ─────────
  /** Tek kaydı DB'ye yaz (kaydet + sync-retry ortak). Başarılıysa kayıt._id + _sync set. */
  private async dbEkle(tip: KayitTip, kayit: IKayit): Promise<boolean> {
    try {
      const snc: any = await this.req.ekle(KOMUT + '.ekle', { vt: this.vt(tip), sahip: this.sahipId(), veri: { id: kayit.id, tip, baslik: kayit.baslik, veri: kayit.veri, tarih: kayit.tarih, sahip: this.sahipId() } });
      kayit._id = snc?.insertedId || snc?._id || snc?.id || true; kayit._sync = true;
      return true;
    } catch { return false; }
  }

  /** Kaydet: local hemen + DB sync (offline ise local kalır, sonra listele'de retry edilir). */
  async kaydet(tip: KayitTip, baslik: string, veri: any): Promise<IKayit> {
    const kayit: IKayit = { id: this.id(), tip, baslik: baslik || '(adsız)', veri, tarih: new Date().toISOString(), _sync: false };
    this.localUpsert(tip, kayit);
    await this.dbEkle(tip, kayit);
    this.localUpsert(tip, kayit);
    return kayit;
  }

  /** Listele: bekleyen (çevrimdışı) kayıtları DB'ye it → DB çek + local birleştir, yeni→eski. */
  async listele(tip: KayitTip): Promise<IKayit[]> {
    let local = this.localOku(tip);
    // SYNC-RETRY: DB'ye yazılamamış (offline) kayıtları şimdi dene
    const bekleyen = local.filter(k => !k._sync);
    if (bekleyen.length) {
      for (const k of bekleyen) if (await this.dbEkle(tip, k)) this.localUpsert(tip, k);
      local = this.localOku(tip);
    }
    try {
      const sahip = this.sahipId();
      const match: any = { vt: this.vt(tip), silindi: { $ne: true } };
      if (sahip) match.sahip = sahip;   // login varsa kullanıcıya izole; yoksa (dev/anonim) tümü
      const db: any[] = await this.req.aggregate(KOMUT + '.aggregate', [
        { $match: match },
        { $sort: { 'veri.tarih': -1 } }, { $limit: 200 },
      ]);
      const dbKayitlar: IKayit[] = (db || []).map(d => ({ ...(d.veri || {}), _id: d._id, _sync: true }));
      const harita = new Map<string, IKayit>();
      for (const k of local) if (k.id) harita.set(k.id, k);
      for (const k of dbKayitlar) if (k.id) harita.set(k.id, k);   // DB öncelikli
      local = [...harita.values()];
      this.localSet(tip, local);
    } catch { /* offline → sadece local */ }
    return local.sort((a, b) => (b.tarih || '').localeCompare(a.tarih || ''));
  }

  /** Sil: local + DB (soft delete). */
  async sil(tip: KayitTip, kayit: IKayit): Promise<void> {
    const l = this.localOku(tip).filter(k => k.id !== kayit.id);
    this.localSet(tip, l);
    try { if (kayit._id) await this.req.guncelle(KOMUT + '.guncelle', { _id: kayit._id }, { silindi: true }); } catch { }
  }

  // ───────── Yükle köprüsü (Kayıtlar paneli → modül) ─────────
  yuklemeyeAl(tip: KayitTip, veri: any): void { this.yuklenecekler.set(tip, veri); }
  yuklenecekAl(tip: KayitTip): any { const v = this.yuklenecekler.get(tip); this.yuklenecekler.delete(tip); return v; }
}
